/**
 * Scoreboard rules. Pure and headless like the rest of `core/`: it never
 * touches `localStorage`, never reads a clock (timestamps arrive as arguments)
 * and never sorts by anything the caller cannot see.
 *
 * Persistence is a seam — see `ScoreRepository`. The browser implementation
 * lives in `src/storage/`.
 */

export interface ScoreEntry {
  name: string;
  score: number;
  lines: number;
  level: number;
  /** Epoch milliseconds, supplied by the caller. */
  playedAt: number;
  /** Missing on legacy entries, which are treated as solo runs. */
  mode?: 'SOLO' | 'BALANCE' | 'TWIN';
}

/** Where a scoreboard is read from and written to. */
export interface ScoreRepository {
  load(): ScoreEntry[];
  save(entries: readonly ScoreEntry[]): void;
  /** Last player name used, if the implementation remembers one. */
  loadName?(): string | null;
  saveName?(name: string): void;
  /**
   * Last shell theme used. Kept as an opaque string so `core/` stays ignorant
   * of what the render layer calls its themes; the caller validates it.
   */
  loadTheme?(): string | null;
  saveTheme?(theme: string): void;
  /**
   * Whether sound is on. Opaque for the same reason as the theme: `core/` does
   * not know an audio layer exists, and the caller validates what comes back.
   */
  loadAudio?(): string | null;
  saveAudio?(audio: string): void;
  /** Persisted master-volume preset, kept opaque to the core. */
  loadVolume?(): string | null;
  saveVolume?(volume: string): void;
  /**
   * Whether the background score is on. Separate from `loadAudio` because
   * muting the music and muting the game are different wishes.
   */
  loadMusic?(): string | null;
  saveMusic?(music: string): void;
}

export const SCOREBOARD = {
  /** How many entries are kept and shown. */
  capacity: 10,
  /** Longest accepted player name. */
  maxNameLength: 12,
  /** Used when a player somehow gets through with nothing typed. */
  fallbackName: 'ANON',
} as const;

/** Characters a name may contain, after upper-casing. */
const NAME_ALLOWED = /[^A-Z0-9 ._-]/g;

/**
 * Normalise raw keyboard input into a storable name: upper case, allowed
 * characters only, collapsed whitespace, trimmed, length-capped. Never throws,
 * may return an empty string (the menu treats that as "not ready to start").
 */
export function sanitizeName(raw: string, maxLength = SCOREBOARD.maxNameLength): string {
  return raw
    .toUpperCase()
    .replace(NAME_ALLOWED, '')
    .replace(/\s+/g, ' ')
    .trimStart()
    .slice(0, maxLength);
}

/** Name as stored: sanitized, trimmed at both ends, never empty. */
export function finalizeName(raw: string, maxLength = SCOREBOARD.maxNameLength): string {
  const clean = sanitizeName(raw, maxLength).trim();
  return clean.length > 0 ? clean : SCOREBOARD.fallbackName;
}

/**
 * Highest score first. Ties break toward the earlier run, so a score already on
 * the board is never pushed down by a later run that merely equals it.
 */
export function compareEntries(a: ScoreEntry, b: ScoreEntry): number {
  if (b.score !== a.score) return b.score - a.score;
  if (b.lines !== a.lines) return b.lines - a.lines;
  return a.playedAt - b.playedAt;
}

export function sortEntries(entries: readonly ScoreEntry[]): ScoreEntry[] {
  return [...entries].sort(compareEntries);
}

export interface InsertResult {
  entries: ScoreEntry[];
  /** 1-based placement, or `null` when the entry did not make the board. */
  rank: number | null;
}

/**
 * Insert a run and cap the board. Returns the new list plus the placement, so
 * the caller can say "3RD" without searching for the entry again.
 */
export function insertEntry(
  entries: readonly ScoreEntry[],
  entry: ScoreEntry,
  capacity = SCOREBOARD.capacity,
): InsertResult {
  const sorted = sortEntries([...entries, entry]);
  const kept = sorted.slice(0, capacity);
  const index = kept.indexOf(entry);
  return { entries: kept, rank: index === -1 ? null : index + 1 };
}

/** Would this score make the board as it stands? */
export function qualifies(
  entries: readonly ScoreEntry[],
  score: number,
  capacity = SCOREBOARD.capacity,
): boolean {
  if (score <= 0) return false;
  if (entries.length < capacity) return true;
  return sortEntries(entries).slice(0, capacity).some((e) => score > e.score);
}

/** Best score on the board, or 0 when it is empty. */
export function bestScore(entries: readonly ScoreEntry[]): number {
  return entries.reduce((best, e) => Math.max(best, e.score), 0);
}

/** Best score for one player, or 0. */
export function personalBest(entries: readonly ScoreEntry[], name: string): number {
  const target = name.trim().toUpperCase();
  return entries.reduce((best, e) => (e.name === target ? Math.max(best, e.score) : best), 0);
}

/** Drop anything that is not a well-formed entry. Used when reading storage. */
export function parseEntries(value: unknown, capacity = SCOREBOARD.capacity): ScoreEntry[] {
  if (!Array.isArray(value)) return [];
  const entries: ScoreEntry[] = [];
  for (const raw of value) {
    if (typeof raw !== 'object' || raw === null) continue;
    const candidate = raw as Record<string, unknown>;
    const name = typeof candidate.name === 'string' ? finalizeName(candidate.name) : null;
    const score = toFiniteInt(candidate.score);
    const lines = toFiniteInt(candidate.lines);
    const level = toFiniteInt(candidate.level);
    const playedAt = toFiniteInt(candidate.playedAt);
    if (name === null || score === null || lines === null || level === null || playedAt === null) {
      continue;
    }
    const mode = candidate.mode === 'TWIN' || candidate.mode === 'BALANCE' ? candidate.mode : 'SOLO';
    entries.push({ name, score, lines, level, playedAt, mode });
  }
  return sortEntries(entries).slice(0, capacity);
}

function toFiniteInt(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return Math.floor(value);
}
